import { useEffect, useMemo, useState } from "react";
import { Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";
import Papa from "papaparse";

import useAxiosPrivate from "../../hooks/useAxiosPrivate";

// Airport type configuration: badge size and color
const airportTypeConfig = {
  small_airport: { size: 16, color: "#22c55e", label: "Small airport" }, // Green
  heliport: { size: 16, color: "#38bdf8", label: "Heliport" }, // Sky blue
  medium_airport: { size: 20, color: "#f59e0b", label: "Medium airport" }, // Amber
  large_airport: { size: 24, color: "#ef4444", label: "Large airport" }, // Red
};

// Material "flight" glyph
const PLANE_PATH =
  "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z";

// Round badge in the type color with a white plane (or an H for heliports),
// a white ring and a soft shadow so it stays readable on any map layer
const createAirportIcon = (type, opacity = 0.7) => {
  const { size, color } = airportTypeConfig[type] || airportTypeConfig.small_airport;
  const glyph =
    type === "heliport"
      ? `<span style="font:800 ${Math.round(size * 0.62)}px/1 Arial,sans-serif;color:#fff;">H</span>`
      : `<svg viewBox="0 0 24 24" width="${Math.round(size * 0.66)}" height="${Math.round(size * 0.66)}" fill="#fff"><path d="${PLANE_PATH}"/></svg>`;

  return L.divIcon({
    className: "airport-icon",
    html: `
      <div style="
        width:${size}px;height:${size}px;box-sizing:border-box;
        display:flex;align-items:center;justify-content:center;
        border-radius:50%;
        background:${color};
        border:1.5px solid rgba(255,255,255,0.9);
        box-shadow:0 1px 4px rgba(0,0,0,0.45);
        opacity:${opacity};
      ">${glyph}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  });
};

function AirportLayer({ allAirports, scale, opacity }) {
  const [visibleAirports, setVisibleAirports] = useState([]);

  const map = useMapEvents({
    moveend: (e) => {
      const bounds = e.target.getBounds();
      const filtered = allAirports.filter((ap) =>
        bounds.contains([ap.lat, ap.lng])
      );
      setVisibleAirports(filtered);
    },
    zoomend: (e) => {
      const bounds = e.target.getBounds();
      const filtered = allAirports.filter((ap) =>
        bounds.contains([ap.lat, ap.lng])
      );
      setVisibleAirports(filtered);
    },
  });

  // Initialize visible airports when allAirports loads or map is ready
  useEffect(() => {
    if (map && allAirports.length > 0) {
      const bounds = map.getBounds();
      const filtered = allAirports.filter((ap) =>
        bounds.contains([ap.lat, ap.lng])
      );
      setVisibleAirports(filtered);
    }
  }, [map, allAirports]);

  if (scale !== undefined && scale < 0.8) {
    return null;
  }

  // Fully transparent: render nothing, so invisible badges can't be clicked either
  if (opacity <= 0) {
    return null;
  }

  return (
    <>
      {visibleAirports.map((ap, i) => (
        <Marker key={i} position={[ap.lat, ap.lng]} icon={createAirportIcon(ap.type, opacity)}>
          <Popup>
            <strong>{ap.name}</strong> <br />
            Code: {ap.code} <br />
            Type: {(airportTypeConfig[ap.type] || airportTypeConfig.small_airport).label}
          </Popup>
        </Marker>
      ))}
    </>
  );
}

// customAirports: airports saved in the database ({ name, code, type, lat, lng }), shown in addition
// to the CSV list. Pass them to keep the list live (Region page); leave it out and the layer loads
// them itself (Radar, Review).
export default function AirportMarker({ center, scale, opacity, customAirports: customAirportsProp }) {
  const axiosPrivate = useAxiosPrivate();
  const [csvAirports, setAllAirports] = useState([]);
  const [loadedAirports, setLoadedAirports] = useState([]);
  const customAirports = customAirportsProp ?? loadedAirports;

  useEffect(() => {
    if (customAirportsProp !== undefined) return;

    let isMounted = true;
    axiosPrivate
      .get("/api/airports")
      .then((res) => {
        if (isMounted) setLoadedAirports(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => console.error("Error fetching airports:", err));

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate, customAirportsProp]);

  useEffect(() => {
    let freqIdents = new Set();

    // Step 1: Load frequencies
    Papa.parse("/data/airport-frequencies.csv", {
      download: true,
      header: true,
      complete: (freqResult) => {
        freqIdents = new Set(freqResult.data.map((row) => row.airport_ident));

        // Step 2: Load airports and filter only those with frequencies
        Papa.parse("/data/airports.csv", {
          download: true,
          header: true,
          complete: (airportResult) => {
            const parsed = airportResult.data
              .filter((row) => row.latitude_deg && row.longitude_deg)
              .filter((row) => freqIdents.has(row.ident))
              .map((row) => ({
                name: row.name,
                lat: parseFloat(row.latitude_deg),
                lng: parseFloat(row.longitude_deg),
                code: row.iata_code || row.ident,
                type: row.type,
              }));
            setAllAirports(parsed);
          },
        });
      },
    });
  }, []);

  const allAirports = useMemo(
    () => [
      ...csvAirports,
      ...(customAirports || []).map((ap) => ({
        name: ap.name,
        lat: ap.lat,
        lng: ap.lng,
        code: ap.code || "-",
        type: ap.type,
      })),
    ],
    [csvAirports, customAirports]
  );

  return <AirportLayer allAirports={allAirports} scale={scale} opacity={opacity} />;
}
